import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * A "Back" handler that always goes somewhere.
 *
 * `navigate(-1)` is a no-op when the current page is the first entry in the
 * tab — a quest link opened from Telegram/WhatsApp/email or in a new tab — so
 * the page's Back button silently did nothing. React Router gives the entry
 * it booted on the key "default"; any in-app navigation gets a generated key,
 * so `key === "default"` is exactly "there is no in-app page to go back to".
 * (`window.history.length` is not: it counts entries from other sites too.)
 */
export function useGoBack(fallback: string): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    if (location.key === "default") {
      navigate(fallback, { replace: true });
    } else {
      navigate(-1);
    }
  }, [location.key, navigate, fallback]);
}
